/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import { ClassAttributes, HTMLAttributes, useCallback, useMemo, useState } from 'react';
import {
  WabaChannelRating,
  WabaChannelStatus,
  WabaIntegration,
  WabaIntegrationPaginationParams,
} from '../../../typings/waba';
import { QSButton, QSTypography } from '../../atoms';
import { Box, ButtonBase, SxProps, TypographyProps } from '@mui/material';
import WabaSelectorModal, { WabaSelectorModalExtraProps } from './modal';
import { getWabaIntegrations } from '../../../requests/waba';
import { selectedWabaAtom, wabaIntegrationsAtomFamily } from '../../../states/waba';
import { DEFAULT_MEMBER, UNLIMITED } from '../../../constants';
import usePaginatedQuery from '../../../hooks/usePaginatedQuery';
import Loader from '../loader';
import WabaSelectorDropdown from './dropdown';
import { useAtom, useAtomValue } from 'jotai';
import useSingleWaba from '../../../hooks/use-single-waba';
import WabaQualityRatingHigh from '../../../icons/WabaQualityRatingHigh';
import WabaQualityRatingLow from '../../../icons/WabaQualityRatingLow';
import WabaQualityRatingMedium from '../../../icons/WabaQualityRatingMedium';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getWabaIntegrationQualityRatingStyles } from '../../../utils/wabaQualityRatingStyles';
import { userProfileAtom } from '../../../states/user';
import WabaIntegrationIcon from '../waba-integration-logo';
import { useWabaInitialSelection } from '../../../hooks/use-waba-initial-selection';

interface WabaSelectorProps {
  onChangeWaba?: (wabas: WabaIntegration) => any;
  headerTitle?: string;
  headerTitleStyles?: TypographyProps;
  allowMultiple?: boolean;
  isDropdown?: boolean;
  setDefaultWaba?: boolean;
  containerProps?: ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement>;
  broadcastMembersCount?: number;
  hideInactiveWabas?: boolean;
  onlyAssignedIntegrations?: boolean;
  showWabaSelectorEvenIfSingle?: boolean;
  inputStylesSx?: SxProps;
  wabaSelectorContainerStyles?: SerializedStyles;
  shouldHideSelectorHeader?: boolean;
  isLoading?: boolean;
  wabaSelectorModalProps?: WabaSelectorModalExtraProps;
  allowedIntegrationIds?: string[];
  showWabaNumber?: boolean;
  hoverBorderColor?: string;
}

const WabaSelector = ({
  onChangeWaba,
  headerTitle = 'Send message via',
  headerTitleStyles,
  allowMultiple = false,
  isDropdown = false,
  setDefaultWaba = true,
  containerProps,
  broadcastMembersCount,
  hideInactiveWabas,
  onlyAssignedIntegrations,
  showWabaSelectorEvenIfSingle = false,
  inputStylesSx,
  wabaSelectorContainerStyles,
  shouldHideSelectorHeader = false,
  isLoading = false,
  wabaSelectorModalProps,
  allowedIntegrationIds,
  showWabaNumber = false,
  hoverBorderColor,
}: WabaSelectorProps) => {
  const [selectedWaba, setSelectedWaba] = useAtom(selectedWabaAtom);
  const userProfile = useAtomValue(userProfileAtom);
  const [searchTerm, setSearchTerm] = useState<string | undefined>('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isMessagingLimitExceeded = (broadcastMembersCount ?? 0) > ((selectedWaba?.messagingLimit as number) ?? 0);

  const { hasMultipleWabas } = useSingleWaba(
    hideInactiveWabas ? WabaChannelStatus.ACTIVE : undefined,
    hideInactiveWabas ? false : null
  );

  const handleWabaSelect = useCallback(
    (waba: WabaIntegration) => {
      setSelectedWaba(waba);
      setIsModalOpen(false);
      onChangeWaba?.(waba);
      localStorage.setItem('selectedWaba', JSON.stringify(waba));
    },
    [onChangeWaba, setSelectedWaba]
  );

  const toggleModal = useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, []);

  const wabaIntegrationsGetter = useCallback(
    async (paginationParams?: WabaIntegrationPaginationParams) => {
      if (!paginationParams) {
        paginationParams = {
          sortAfter: undefined,
          searchTerm: typeof searchTerm === 'string' && searchTerm.trim().length > 0 ? searchTerm.trim() : '',
          status: hideInactiveWabas ? WabaChannelStatus.ACTIVE : undefined,
          disconnectedFromMeta: hideInactiveWabas ? false : null,
          sendOnlyAssignedIntegrations: onlyAssignedIntegrations,
          hasMore: false,
        };
      }
      const data = await getWabaIntegrations(paginationParams);
      const { integrations, nextSortAfter, hasMore } = data || {};

      return {
        data: integrations,
        paginationData: {
          ...paginationParams,
          sortAfter: nextSortAfter ?? paginationParams?.nextSortAfter,
          searchTerm: typeof searchTerm === 'string' && searchTerm.trim().length > 0 ? searchTerm.trim() : '',
          status: hideInactiveWabas ? WabaChannelStatus.ACTIVE : undefined,
          disconnectedFromMeta: hideInactiveWabas ? false : null,
          sendOnlyAssignedIntegrations: onlyAssignedIntegrations,
          hasMore: hasMore ?? paginationParams.hasMore,
        } as WabaIntegrationPaginationParams,
      };
    },
    [searchTerm, hideInactiveWabas, onlyAssignedIntegrations]
  );

  const wabaIntegrationsAtom = useMemo(() => {
    return wabaIntegrationsAtomFamily({
      id: DEFAULT_MEMBER,
    });
  }, []);

  const {
    fetchMore: wabaIntegrationsFetchMore,
    requestData: {
      loading: wabaIntegrationsLoading,
      isFetchingMore: fetchingMoreWabaIntegrations,
      data: wabaIntegrationsData,
    },
  } = usePaginatedQuery<WabaIntegration, WabaIntegrationPaginationParams>({
    requestAtom: wabaIntegrationsAtom,
    getter: wabaIntegrationsGetter,
  });

  useWabaInitialSelection({
    wabaIntegrationsData,
    setSelectedWaba,
    enabled: setDefaultWaba,
    allowedIntegrationIds: allowedIntegrationIds ?? null,
    userProfile,
    onChange: onChangeWaba,
    runOnlyOnce: true,
  });

  const wabaIntegrationsDataAssignedList = wabaIntegrationsData?.filter(
    (integration) =>
      integration.isSelfAssigned && (!allowedIntegrationIds || allowedIntegrationIds.includes(integration.id))
  );

  if (!hasMultipleWabas && !showWabaSelectorEvenIfSingle) {
    return null;
  }

  if (wabaIntegrationsLoading && !wabaIntegrationsData) {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
        `}
      >
        <Loader secondary="Loading integrations..." />
      </div>
    );
  }
  const renderWabaSelection = () => {
    if (
      Array.isArray(wabaIntegrationsDataAssignedList) &&
      wabaIntegrationsDataAssignedList.length == 1 &&
      !showWabaSelectorEvenIfSingle
    ) {
      return null;
    }
    return (
      <div
        css={css`
          display: flex;
          padding: 20px;
          flex-direction: column;
          align-items: flex-start;
          gap: 12px;
          align-self: stretch;
          border-top: 1px solid #e0e0e9;
          border-bottom: 1px solid #e0e0e9;
          ${wabaSelectorContainerStyles}
        `}
      >
        {!shouldHideSelectorHeader && (
          <QSTypography
            css={css`
              font-size: 14px;
              font-style: normal;
              font-weight: 600;
              line-height: normal;
            `}
            {...headerTitleStyles}
          >
            {headerTitle ?? 'Send message via'}
          </QSTypography>
        )}
        {!selectedWaba ? (
          <ButtonBase
            onClick={toggleModal}
            css={css`
              display: flex;
              align-items: center;
              padding: 12px;
              width: 100%;
              font-size: 16px;
              font-style: normal;
              font-weight: 700;
              color: #538d6f;
            `}
          >
            Select integration
          </ButtonBase>
        ) : (
          <Box
            css={css`
              width: 100%;
              display: flex;
              flex-direction: column;
              gap: 16px;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 12px;
                align-self: stretch;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 16px;
                  flex: 1 0 0;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex: 1;
                  `}
                >
                  <WabaIntegrationIcon
                    wabaIntegrationLogo={selectedWaba.logo ?? ''}
                    wabaIntegrationName={
                      selectedWaba.displayName ?? selectedWaba.name ?? selectedWaba.phoneNumber ?? ''
                    }
                    channelType={selectedWaba.type}
                  />
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: flex-start;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 8px;
                      `}
                    >
                      <QSTypography
                        css={css`
                          font-size: 14px;
                          font-style: normal;
                          font-weight: 500;
                          line-height: normal;
                        `}
                      >
                        {selectedWaba.displayName ?? selectedWaba.name ?? selectedWaba.phoneNumber ?? ''}
                      </QSTypography>
                      {selectedWaba.qualityRating && (
                        <Box
                          css={css(
                            getWabaIntegrationQualityRatingStyles(selectedWaba.qualityRating),
                            css`
                              display: flex;
                              flex-wrap: wrap;
                              justify-content: flex-start;
                              align-items: center;
                              column-gap: 4px;
                              padding: 3px 9px;
                              border-radius: 12px;
                            `
                          )}
                        >
                          {selectedWaba.qualityRating === WabaChannelRating.HIGH ? (
                            <WabaQualityRatingHigh
                              css={css`
                                margin-top: 4px;
                              `}
                            />
                          ) : selectedWaba.qualityRating === WabaChannelRating.LOW ? (
                            <WabaQualityRatingLow />
                          ) : selectedWaba.qualityRating === WabaChannelRating.MEDIUM ? (
                            <WabaQualityRatingMedium />
                          ) : null}
                          <QSTypography
                            css={css`
                              font-size: 12px;
                              font-weight: 500;
                              word-wrap: break-word;
                              word-break: break-all;
                            `}
                          >
                            {selectedWaba.qualityRating}
                          </QSTypography>
                        </Box>
                      )}
                    </div>
                    <QSTypography
                      css={css`
                        font-size: 14px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: normal;
                      `}
                    >
                      {selectedWaba.phoneNumber}
                    </QSTypography>
                    {(typeof selectedWaba.messagingLimit === 'number' || selectedWaba.messagingLimit === UNLIMITED) &&
                      !isMessagingLimitExceeded && (
                        <Box
                          css={css`
                            display: flex;
                            align-items: center;
                            gap: 8px;
                          `}
                        >
                          <MessageOutlinedIcon
                            css={css`
                              height: 16px;
                              min-height: 16px;
                              width: 16px;
                              min-width: 16px;
                              align-self: flex-start;
                              color: #7a7a7a;
                            `}
                          />
                          <QSTypography
                            css={css`
                              color: #7a7a7a;
                              font-size: 14px;
                              font-style: normal;
                              font-weight: 500;
                              line-height: normal;
                            `}
                          >
                            Message limit:{' '}
                            <span
                              css={css`
                                color: #2d2d2d;
                              `}
                            >
                              {selectedWaba.messagingLimit.toString()} / 24hrs
                            </span>
                          </QSTypography>
                        </Box>
                      )}
                  </div>
                </div>
              </div>
              {wabaIntegrationsDataAssignedList && wabaIntegrationsDataAssignedList?.length > 1 && (
                <QSButton
                  css={css`
                    outline: none;
                    border: none;
                    box-shadow: none;

                    &:hover {
                      outline: none;
                      border: none;
                      box-shadow: none;
                    }
                  `}
                  variant="text"
                  onClick={toggleModal}
                  disabled={isLoading}
                >
                  {isLoading ? <Loader size={24} /> : 'Change'}
                </QSButton>
              )}
            </div>
            {isMessagingLimitExceeded && (
              <Box
                css={css`
                  background-color: rgba(231, 149, 28, 0.12);
                  display: flex;
                  gap: 8px;
                  padding: 12px 16px;
                  border-radius: 12px;
                `}
              >
                <WarningAmberIcon
                  css={css`
                    height: 20px;
                    min-height: 20px;
                    width: 20px;
                    min-width: 20px;
                    align-self: flex-start;
                    color: #e7951c;
                  `}
                />
                <Box
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                  `}
                >
                  <QSTypography
                    css={css`
                      color: #2d2d2d;
                      font-size: 14px;
                      font-weight: 500;
                    `}
                  >
                    Messaging limit exceeded ({selectedWaba.messagingLimit?.toString()} conversations / 24hrs)
                  </QSTypography>
                  <QSTypography
                    css={css`
                      color: #7a7a7a;
                      font-size: 12px;
                      font-weight: 500;
                    `}
                  >
                    Your message will be sent to the first 1000 customers only.
                  </QSTypography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </div>
    );
  };
  if (isDropdown) {
    return (
      <WabaSelectorDropdown
        inputStylesSx={inputStylesSx}
        setSelectedWaba={handleWabaSelect}
        wabaIntegrationsFetchMore={wabaIntegrationsFetchMore}
        wabaIntegrationsData={wabaIntegrationsData}
        fetchingMoreWabaIntegrations={fetchingMoreWabaIntegrations}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedWaba={selectedWaba}
        headerTitle={headerTitle}
        headerTitleStyles={headerTitleStyles}
        containerProps={containerProps}
        showWabaNumber={showWabaNumber}
        hoverBorderColor={hoverBorderColor}
      />
    );
  }

  return (
    <>
      {isModalOpen ? (
        <WabaSelectorModal
          open={true}
          onClose={() => setIsModalOpen(false)}
          allowMultiple={allowMultiple}
          setSelectedWaba={handleWabaSelect}
          hideInactiveWabas={hideInactiveWabas}
          onlyAssignedIntegrations={onlyAssignedIntegrations}
          wabaSelectorModalExtraProps={{
            selectedWaba: selectedWaba,
            ...(wabaSelectorModalProps || {}),
          }}
        />
      ) : null}

      {renderWabaSelection()}
    </>
  );
};

export default WabaSelector;
